import { useState, useEffect } from 'react'
import { Users, Plus, Pencil, Trash2, RefreshCw, EyeOff } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { BottomNav } from '@/components/layout/BottomNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/useToast'
import { FunctionsService } from '@/services/functions.service'

interface Seller {
  id: string
  name: string
  shortName?: string | null
  role: 'principal' | 'secundario' | 'ninguno'
  percentage: number
  hasCommission: boolean
  active?: boolean
}

const EMPTY_SELLER: Seller = {
  id: '',
  name: '',
  shortName: '',
  role: 'secundario',
  percentage: 0,
  hasCommission: false,
  active: true,
}

const ROLE_LABELS: Record<string, string> = {
  principal: 'Principal',
  secundario: 'Secundario',
  ninguno: 'Ninguno',
}

const ROLE_BADGE: Record<string, string> = {
  principal: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
  secundario: 'bg-purple-500/20 text-purple-300 border border-purple-500/30',
  ninguno: 'bg-white/10 text-white/40 border border-white/10',
}

export default function VendedoresPage() {
  const [sellers, setSellers] = useState<Seller[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [form, setForm] = useState<Seller>(EMPTY_SELLER)
  const [originalId, setOriginalId] = useState('')
  const [isNew, setIsNew] = useState(false)
  const [showHidden, setShowHidden] = useState(false)
  const { toast, toasts, dismiss } = useToast()

  useEffect(() => {
    loadSellers()
  }, [])

  async function loadSellers() {
    setLoading(true)
    const result = await FunctionsService.getSellers()
    if (result.data) {
      const sorted = [...result.data.sellers].sort((a, b) =>
        a.name.localeCompare(b.name)
      )
      setSellers(sorted as Seller[])
    } else if (result.error) {
      toast({ title: 'Error al cargar', description: result.error, variant: 'destructive' })
    }
    setLoading(false)
  }

  function openAdd() {
    setForm({ ...EMPTY_SELLER })
    setOriginalId('')
    setIsNew(true)
    setDialogOpen(true)
  }

  function openEdit(seller: Seller) {
    setForm({ ...seller, shortName: seller.shortName ?? '', active: seller.active !== false })
    setOriginalId(seller.id)
    setIsNew(false)
    setDialogOpen(true)
  }

  async function handleSave() {
    if (!form.id.trim() || !form.name.trim()) {
      toast({ title: 'Faltan campos', description: 'ID y nombre son obligatorios', variant: 'destructive' })
      return
    }
    // Si al editar cambió el ID de Shopify, el backend migra el documento
    // (crea el nuevo, borra el viejo) preservando sus datos.
    const idChanged = !isNew && form.id.trim() !== originalId
    setSaving(true)
    const result = await FunctionsService.saveSeller({
      ...form,
      shortName: form.shortName?.trim() || undefined,
      active: form.active !== false,
      previousId: idChanged ? originalId : undefined,
    })
    if (result.data?.success) {
      toast({ title: 'Guardado', description: `${form.name} guardado correctamente` })
      setDialogOpen(false)
      await loadSellers()
    } else {
      toast({ title: 'Error al guardar', description: result.error ?? 'Error desconocido', variant: 'destructive' })
    }
    setSaving(false)
  }

  async function handleToggleActive(seller: Seller) {
    const newActive = seller.active === false ? true : false
    const result = await FunctionsService.saveSeller({
      id: seller.id,
      name: seller.name,
      shortName: seller.shortName ?? undefined,
      role: seller.role,
      percentage: seller.percentage,
      hasCommission: seller.hasCommission,
      active: newActive,
    })
    if (result.data?.success) {
      setSellers(prev =>
        prev.map(s => s.id === seller.id ? { ...s, active: newActive } : s)
      )
    } else {
      toast({ title: 'Error', description: result.error ?? 'No se pudo actualizar', variant: 'destructive' })
    }
  }

  async function handleDelete() {
    if (!deleteId) return
    const result = await FunctionsService.deleteSeller(deleteId)
    if (result.data?.success) {
      toast({ title: 'Eliminado', description: 'Vendedor eliminado correctamente' })
      setDeleteId(null)
      await loadSellers()
    } else {
      toast({ title: 'Error al eliminar', description: result.error ?? 'Error desconocido', variant: 'destructive' })
    }
  }

  const visibleSellers = showHidden ? sellers : sellers.filter(s => s.active !== false)
  const hiddenCount = sellers.filter(s => s.active === false).length

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map(t => (
          <div
            key={t.id}
            onClick={() => dismiss(t.id!)}
            className={`cursor-pointer rounded-lg px-4 py-3 shadow-lg text-sm max-w-xs ${
              t.variant === 'destructive' ? 'bg-red-900/90 text-red-100' : 'bg-gray-800 text-white'
            }`}
          >
            <p className="font-semibold">{t.title}</p>
            {t.description && <p className="text-white/70 mt-0.5">{t.description}</p>}
          </div>
        ))}
      </div>

      <div className="mx-auto w-full max-w-[1600px] px-4 py-8 pb-24 sm:px-6 lg:px-10">
        <Header />

        {/* Page header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-blue-400" />
            <span className="text-white/50 text-sm">
              {visibleSellers.length} vendedor{visibleSellers.length !== 1 ? 'es' : ''}
              {hiddenCount > 0 && !showHidden && (
                <span className="ml-2 text-white/30">({hiddenCount} oculto{hiddenCount !== 1 ? 's' : ''})</span>
              )}
            </span>
            {hiddenCount > 0 && (
              <button
                onClick={() => setShowHidden(v => !v)}
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  showHidden
                    ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                    : 'bg-white/5 border-white/10 text-white/40 hover:text-white/60'
                }`}
              >
                <EyeOff className="h-3 w-3" />
                {showHidden ? 'Mostrando ocultos' : 'Mostrar ocultos'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadSellers}
              disabled={loading}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button
              size="sm"
              onClick={openAdd}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center text-white/40 py-16">Cargando vendedores…</div>
        ) : (
          <div className="rounded-xl border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="px-4 py-3 text-left text-white/50 font-medium">ID Shopify</th>
                  <th className="px-4 py-3 text-left text-white/50 font-medium">Nombre</th>
                  <th className="px-4 py-3 text-left text-white/50 font-medium hidden sm:table-cell">Nombre corto</th>
                  <th className="px-4 py-3 text-left text-white/50 font-medium">Rol</th>
                  <th className="px-4 py-3 text-right text-white/50 font-medium hidden md:table-cell">Meta %</th>
                  <th className="px-4 py-3 text-center text-white/50 font-medium hidden lg:table-cell">Comisión</th>
                  <th className="px-4 py-3 text-center text-white/50 font-medium">Activo</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {visibleSellers.map(seller => {
                  const isActive = seller.active !== false
                  return (
                    <tr
                      key={seller.id}
                      className={`hover:bg-white/5 transition-colors ${!isActive ? 'opacity-40' : ''}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-white/40">{seller.id}</td>
                      <td className="px-4 py-3 font-medium">{seller.name}</td>
                      <td className="px-4 py-3 text-white/60 hidden sm:table-cell">
                        {seller.shortName || <span className="text-white/25">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE[seller.role]}`}>
                          {ROLE_LABELS[seller.role]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-white/60 hidden md:table-cell">
                        {seller.percentage > 0 ? `${seller.percentage}%` : <span className="text-white/25">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center hidden lg:table-cell">
                        <span className={seller.hasCommission ? 'text-green-400 text-sm' : 'text-white/25 text-sm'}>
                          {seller.hasCommission ? '✓' : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch
                          checked={isActive}
                          onCheckedChange={() => handleToggleActive(seller)}
                          className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/20"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-white/40 hover:text-white hover:bg-white/10"
                            onClick={() => openEdit(seller)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-400/50 hover:text-red-400 hover:bg-red-500/10"
                            onClick={() => setDeleteId(seller.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {visibleSellers.length === 0 && (
              <p className="text-center text-white/30 py-12">
                {showHidden ? 'No hay vendedores registrados' : 'No hay vendedores activos'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isNew ? 'Agregar vendedor' : 'Editar vendedor'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="v-id">ID de Shopify *</Label>
              <Input
                id="v-id"
                value={form.id}
                onChange={e => setForm(f => ({ ...f, id: e.target.value.trim() }))}
                placeholder="ej. 109093847129"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25"
              />
              {!isNew && form.id.trim() !== originalId && (
                <p className="text-xs text-amber-400/80 mt-0.5">
                  Cambiar el ID migrará el registro (de {originalId || '—'} a {form.id.trim() || '—'})
                  y conservará sus datos.
                </p>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="v-name">Nombre completo *</Label>
              <Input
                id="v-name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="ej. Roberto M. Colón Sánchez"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25"
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="v-short">Nombre corto</Label>
              <Input
                id="v-short"
                value={form.shortName ?? ''}
                onChange={e => setForm(f => ({ ...f, shortName: e.target.value }))}
                placeholder="ej. Roberto C."
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Rol *</Label>
              <Select
                value={form.role}
                onValueChange={v => setForm(f => ({ ...f, role: v as Seller['role'] }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/10 text-white">
                  <SelectItem value="principal">Principal</SelectItem>
                  <SelectItem value="secundario">Secundario</SelectItem>
                  <SelectItem value="ninguno">Ninguno</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="v-pct">Porcentaje de meta (%)</Label>
              <Input
                id="v-pct"
                type="number"
                min={0}
                max={100}
                value={form.percentage}
                onChange={e => setForm(f => ({ ...f, percentage: Math.max(0, Math.min(100, Number(e.target.value))) }))}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3">
              <Label htmlFor="v-comm" className="cursor-pointer">¿Tiene comisión?</Label>
              <Switch
                id="v-comm"
                checked={form.hasCommission}
                onCheckedChange={v => setForm(f => ({ ...f, hasCommission: v }))}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3">
              <div>
                <Label htmlFor="v-active" className="cursor-pointer">Vendedor activo</Label>
                <p className="text-xs text-white/40 mt-0.5">Ocultar lo excluye de las vistas</p>
              </div>
              <Switch
                id="v-active"
                checked={form.active !== false}
                onCheckedChange={v => setForm(f => ({ ...f, active: v }))}
                className="data-[state=checked]:bg-emerald-500"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-gray-900 border-white/10 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar eliminación</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm py-2">
            ¿Eliminar este vendedor permanentemente? Si solo quieres ocultarlo, usa el switch
            de <strong className="text-white/80">Activo</strong> en su fila.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteId(null)}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  )
}
